import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography, Tooltip } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { FS_SM, ICON_LG } from '../../common/theme'
import { STATE_DEFS } from '../../common/constants'
import TraceArc from '../../comps/TraceArc'
import ScoreArc from '../../comps/ScoreArc'
import StatePill from '../../comps/StatePill'
import TaWordCount from '../../comps/TaWordCount'
import TabHeader from '../../comps/TabHeader'
import { DataTable, type DataTableColumn, type DataTableSort } from '../../comps/DataTable'
import type { Project, Question } from '../../types'

// Card-style header: small uppercase label.
function CardLabel({ label }: { label: string }) {
  return (
    <Typography variant="caption" sx={{
      fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em',
      opacity: 0.6, fontWeight: 700,
    }}>
      {label}
    </Typography>
  )
}

const ALL_KEY = '__all__'
const UNSECTIONED_KEY = '__unsectioned__'

type SortKey = 'index' | 'numbering' | 'section' | 'question' | 'state' | 'replylimit' | 'sim' | 'score'

interface BidOverviewTabProps {
  project: Project | null
  onSelectQuestion?: (q: Question) => void
  onGoToBidSources?: () => void
  onGoToWinThemes?: () => void
  onGoToScoringTemplates?: () => void
}

// Plain-text version of the question html, for table sort + cell display.
function plainText(html?: string | null): string {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent ?? '').trim()
}

const STATE_ORDER: Record<string, number> = {
  todo: 0, draft: 1, review: 2, done: 3,
}

interface RowData {
  q: Question
  ordinal: number
  index: number
  numbering: string
  section: string
  questionText: string
  state: string
  // Answer-size target - `limit` is the raw count from `replylimit.answerlimit`
  // and `isCharCount` flips the displayed unit between "chars" and "words".
  limit: number | null
  isCharCount: boolean
  sim: number | null
  score: number | null
}

export default function BidOverviewTab({
  project, onSelectQuestion,
  onGoToBidSources, onGoToWinThemes, onGoToScoringTemplates,
}: BidOverviewTabProps) {
  const questions: Question[] = (project?.questions ?? []) as Question[]

  const [sort, setSort] = useState<DataTableSort>({ colId: 'index', dir: 'asc' })

  // Section filter - same model as BidQuestionsTab. `undefined` = All,
  // empty string = Unsectioned, otherwise the section name.
  const [sectionFilter, setSectionFilter] = useState<string | undefined>(undefined)

  const { sectionList, hasUnsectioned } = useMemo(() => {
    const seen = new Set<string>()
    const list: string[] = []
    let unsectioned = false
    for (const q of questions) {
      const sec = (q as any).section as string | null | undefined
      if (sec) {
        if (!seen.has(sec)) { seen.add(sec); list.push(sec) }
      } else {
        unsectioned = true
      }
    }
    return { sectionList: list, hasUnsectioned: unsectioned }
  }, [questions])

  const showSectionTabs = sectionList.length > 0

  const activeSectionKey =
    sectionFilter === undefined ? ALL_KEY :
      sectionFilter === '' ? UNSECTIONED_KEY :
        sectionFilter

  const sectionTabs = [
    { key: ALL_KEY, label: 'All' },
    ...sectionList.map(s => ({ key: s, label: s })),
    ...(hasUnsectioned ? [{ key: UNSECTIONED_KEY, label: 'Unsectioned' }] : []),
  ]

  const handleSectionTabChange = (key: string) => {
    if (key === ALL_KEY) setSectionFilter(undefined)
    else if (key === UNSECTIONED_KEY) setSectionFilter('')
    else setSectionFilter(key)
  }

  // Filter applies to the table only - stats stay over the entire bid.
  const filteredQuestions: Question[] = useMemo(() => {
    if (sectionFilter === undefined) return questions
    return questions.filter((q: any) => (q.section ?? '') === sectionFilter)
  }, [questions, sectionFilter])

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = questions.length
    const stateCounts: Record<string, number> = { todo: 0, draft: 0, review: 0, done: 0 }
    let withAnswer = 0
    let withTrace = 0
    let withScore = 0
    let simSum = 0, simN = 0
    let scoreSum = 0, scoreN = 0

    for (const q of questions) {
      const s = (q as any).state ?? 'todo'
      if (stateCounts[s] !== undefined) stateCounts[s]++
      const ao: any = (q as any).answerobj
      if (ao?.answer) withAnswer++
      if (ao?.answerstructure) withTrace++
      const sim = ao?.simint
      if (typeof sim === 'number' && sim > 0) { simSum += sim; simN++ }
      const be: any = (q as any).bideval
      if (be) withScore++
      const score = be?.scoreint
      if (typeof score === 'number' && score > 0) { scoreSum += score; scoreN++ }
    }

    const avgSim = simN > 0 ? Math.round(simSum / simN) : 0
    const avgScore = scoreN > 0 ? Math.round(scoreSum / scoreN) : 0
    const tags = (project as any).projecttags ?? []
    const buyerTags = tags.filter((t: any) => t.buyer).length
    const supplierTags = tags.length - buyerTags
    const winThemes = (project as any).winthemetemplates?.length ?? 0
    const scoringTemplates = (project as any).scoringtemplates?.length ?? 0

    return { total, stateCounts, withAnswer, withTrace, withScore, avgSim, avgScore, buyerTags, supplierTags, totalTags: tags.length, winThemes, scoringTemplates }
  }, [questions, project])

  // ── Rows + sort ───────────────────────────────────────────────────────────
  const sortedRows: RowData[] = useMemo(() => {
    const rows: RowData[] = filteredQuestions.map((q, i) => {
      const ao: any = (q as any).answerobj
      const be: any = (q as any).bideval
      const rl: any = (q as any).replylimit
      return {
        q,
        ordinal: i + 1,
        index: (q as any).index ?? i + 1,
        numbering: (q as any).numbering ?? '',
        section: (q as any).section ?? '',
        questionText: plainText((q as any).question),
        state: (q as any).state ?? 'todo',
        limit: typeof rl?.answerlimit === 'number' ? rl.answerlimit : null,
        isCharCount: !!rl?.charcount,
        sim: typeof ao?.simint === 'number' ? ao.simint : null,
        score: typeof be?.scoreint === 'number' ? be.scoreint : null,
      }
    })

    const dir = sort.dir === 'asc' ? 1 : -1
    const cmpStr = (a: string, b: string) => a.localeCompare(b)
    const cmpNum = (a: number | null, b: number | null) => {
      if (a == null && b == null) return 0
      if (a == null) return 1
      if (b == null) return -1
      return a - b
    }

    const primary = (a: RowData, b: RowData): number => {
      switch (sort.colId as SortKey) {
        case 'index': return (a.ordinal - b.ordinal) * dir
        case 'numbering': return cmpStr(a.numbering, b.numbering) * dir
        case 'section': return cmpStr(a.section, b.section) * dir
        case 'question': return cmpStr(a.questionText, b.questionText) * dir
        case 'state': return ((STATE_ORDER[a.state] ?? 99) - (STATE_ORDER[b.state] ?? 99)) * dir
        case 'replylimit': return cmpNum(a.limit, b.limit) * dir
        case 'sim': return cmpNum(a.sim, b.sim) * dir
        case 'score': return cmpNum(a.score, b.score) * dir
        default: return 0
      }
    }

    rows.sort((a, b) => {
      const p = primary(a, b)
      if (p !== 0) return p
      // Tie-break on question number (ordinal) ascending.
      return a.ordinal - b.ordinal
    })
    return rows
  }, [filteredQuestions, sort])

  // ── Columns for the DataTable ─────────────────────────────────────────────
  const cols: DataTableColumn<RowData>[] = useMemo(() => [
    {
      id: 'index', label: '#', width: 56,
      render: (row) => <Typography sx={{ fontSize: FS_SM, opacity: 0.65 }}>{row.ordinal}</Typography>,
    },
    {
      id: 'numbering', label: 'Numbering', width: 110,
      render: (row) => (
        <Typography sx={{ fontSize: FS_SM, fontWeight: row.numbering ? 700 : 400, opacity: row.numbering ? 1 : 0.4 }}>
          {row.numbering || '-'}
        </Typography>
      ),
    },
    {
      id: 'section', label: 'Section', width: 140, align: 'center',
      render: (row) => row.section ? (
        <Box sx={{ px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: 'action.selected', display: 'inline-block', maxWidth: '100%' }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.section}
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: FS_SM, opacity: 0.35 }}>-</Typography>
      ),
    },
    {
      id: 'question', label: 'Question', width: 0, flex: 1, minWidth: 80,
      render: (row) => (
        <Typography sx={{ fontSize: FS_SM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.questionText || <Box component="span" sx={{ opacity: 0.4 }}>(no text)</Box>}
        </Typography>
      ),
    },
    {
      id: 'state', label: 'State', width: 110, align: 'center',
      render: (row) => <StatePill state={row.state} />,
    },
    {
      // Shared TaWordCount renders "{count} / {limit}" with the icon
      // matching word/char mode and an over-limit warning. The wizard
      // affordance is omitted since the table row isn't the right place
      // to launch the wizard from.
      id: 'replylimit', label: 'Length', width: 110, align: 'center',
      render: (row) => (
        <TaWordCount
          structure={row.q.answerobj?.answerstructure}
          replylimit={(row.q as any).replylimit}
          question={row.q}
        />
      ),
    },
    {
      id: 'sim', label: 'Trace %', width: 90, align: 'center',
      render: (row) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <TraceArc value={row.sim} />
        </Box>
      ),
    },
    {
      id: 'score', label: 'Score %', width: 90, align: 'center',
      render: (row) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <ScoreArc value={row.score} />
        </Box>
      ),
    },
  ], [])

  // ── Top vs table resize ───────────────────────────────────────────────
  // Until the user actually drags the divider, the top panel sizes to its
  // content (flex: 0 0 auto). The first drag flips `hasResized` and from
  // then on we lock to an explicit pixel height.
  const containerRef = useRef<HTMLDivElement>(null)
  const [topHeight, setTopHeight] = useState(280)
  const [hasResized, setHasResized] = useState(false)
  const isResizingRef = useRef(false)
  const startYRef = useRef(0)
  const startHeightRef = useRef(280)

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientY - startYRef.current
      const maxH = (containerRef.current?.clientHeight ?? window.innerHeight) - 120
      setTopHeight(Math.max(80, Math.min(maxH, startHeightRef.current + delta)))
    }
    const onUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isResizingRef.current = true
    startYRef.current = e.clientY
    // Seed the locked height from the panel's current rendered height the
    // first time the user drags (it's been auto-sized to content until now).
    const measured = containerRef.current?.firstElementChild instanceof HTMLElement
      ? (containerRef.current.firstElementChild as HTMLElement).offsetHeight
      : topHeight
    startHeightRef.current = hasResized ? topHeight : measured
    if (!hasResized) {
      setTopHeight(measured)
      setHasResized(true)
    }
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
  }

  return (
    <Box ref={containerRef} data-testid="bo_root" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Top: intro + stat cards (resizable) ──────────────────────────── */}
      <Box sx={{
        flex: hasResized ? `0 0 ${topHeight}px` : '0 0 auto',
        minHeight: 80, overflowY: 'auto',
      }}>

        {/* ── Intro ────────────────────────────────────────────────────────── */}

        <Box sx={{ px: 1.75, pt: 1.5, pb: 0.75, flexShrink: 0, display: 'inline-flex' }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.2 }}>
            Bid Overview
          </Typography>
        </Box>
        {/* <Tooltip title="A snapshot of the bid's health. The cards below show how many questions you have and where each one sits in the workflow, how strongly the answers are grounded in your sources (trace) and how the scoring rubric rates them. Click any card to jump to its underlying setup, or click a row in the table to open that question.">
      </Tooltip> */}

        {/* ── Stat cards ──────────────────────────────────────────────────── */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 1.25,
          px: 1.5, pb: 1.5,
          flexShrink: 0,
        }}>
          <StatCard
            label="Questions"
            primary={String(stats.total)}
            secondary={`${stats.withAnswer} answered · ${stats.withTrace} traced`}
            description="Total questions imported into this bid. ‘Answered’ have a generated reply; ‘traced’ also have a sentence-level provenance match against your sources."
          />

          {/* State breakdown */}
          <Tooltip title="Workflow stage of every question. Set the state from each row to track progress: Todo → Draft (answer generated) → Review (eyes on it) → Done (signed off).">
            <Box sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <CardLabel label="Question States" />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                {STATE_DEFS.filter(s => (s.key as string) !== 'processing').map(s => (
                  <StatePill
                    key={s.key}
                    state={s.key}
                    count={stats.stateCounts[s.key] ?? 0}
                  />
                ))}
              </Box>
            </Box>
          </Tooltip>

          {/* Avg trace */}
          <Tooltip title="How well each answer is grounded in your bid sources. A higher % means more sentences in the answer match content from your uploaded documents.">
            <Box sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TraceArc value={stats.avgSim} />
              <Box>
                <CardLabel label="Avg Trace" />
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.1 }}>
                  {stats.avgSim}%
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.62rem', opacity: 0.55 }}>
                  over {stats.withTrace} traced
                </Typography>
              </Box>
            </Box>
          </Tooltip>

          {/* Avg score */}
          <Tooltip title="Estimated mark from running each answer through the scoring template. Treat it as a guide, not the buyer's actual score.">
            <Box sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScoreArc value={stats.avgScore} />
              <Box>
                <CardLabel label="Avg Score" />
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.1 }}>
                  {stats.avgScore}%
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.62rem', opacity: 0.55 }}>
                  over {stats.withScore} scored
                </Typography>
              </Box>
            </Box>
          </Tooltip>

          {/* Bid sources - clickable, jumps to Manage > Bid Sources */}
          <Tooltip title="Documents the bid pulls from when generating answers. Buyer sources are the tender pack; supplier sources are your own knowledge.">
            <Box
              data-testid="bo_bidsources_card"
              onClick={onGoToBidSources}
              sx={{
                p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1,
                cursor: onGoToBidSources ? 'pointer' : 'default',
                transition: 'border-color 0.12s, background-color 0.12s',
                '&:hover': onGoToBidSources ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                display: 'flex', flexDirection: 'column', gap: 0.25,
              }}
            >
              <CardLabel label="Bid Sources" />
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.1 }}>
                {stats.totalTags}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.62rem', opacity: 0.55, display: 'block' }}>
                {stats.buyerTags} buyer · {stats.supplierTags} supplier
              </Typography>
              {onGoToBidSources && (
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', mt: 0.25, display: 'block' }}>
                  Open bid sources →
                </Typography>
              )}
            </Box>
          </Tooltip>

          <StatCard
            label="Win Themes"
            primary={String(stats.winThemes)}
            secondary={stats.winThemes === 1 ? 'set' : 'sets'}
            description="The strategic messages you want to weave through every answer (e.g. ‘proven in defence’, ‘lowest TCO’). Each set is a reusable list."
            onClick={onGoToWinThemes}
            linkLabel="Open win themes →"
            testId="bo_winthemes_card"
          />
          <StatCard
            label="Scoring Templates"
            primary={String(stats.scoringTemplates)}
            secondary={stats.scoringTemplates === 1 ? 'template' : 'templates'}
            description="The rubrics used to estimate the score for each answer - typically modelled on the buyer's published evaluation criteria."
            onClick={onGoToScoringTemplates}
            linkLabel="Open scoring templates →"
            testId="bo_scoringtemplates_card"
          />
        </Box>

      </Box>

      {/* Visible resize handle between the top panel and the question table. */}
      <Box
        onMouseDown={handleResizeStart}
        data-testid="bo_resize_handle"
        sx={{
          height: 7, flexShrink: 0,
          cursor: 'row-resize',
          borderTop: 1, borderBottom: 1, borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <MoreHorizIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
      </Box>

      {/* ── Question table ─────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>

        {/* Section tabs (only shown when the bid has user-defined sections) */}
        {showSectionTabs && (
          <TabHeader
            tabs={sectionTabs}
            active={activeSectionKey}
            onChange={handleSectionTabChange}
            testIdPrefix="bo_section"
          />
        )}

        <DataTable<RowData>
          rows={sortedRows}
          cols={cols}
          rowKey={(row) => (row.q as any).questionsha ?? row.ordinal}
          rowTestId="bo_question_row"
          rowDataAttrs={(row) => ({ 'data-questionsha': (row.q as any).questionsha })}
          onRowClick={onSelectQuestion ? (row) => onSelectQuestion(row.q) : undefined}
          sort={sort}
          onSortChange={(colId, dir) => setSort({ colId, dir })}
          emptyContent={
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.45 }}>
                No questions in this bid yet.
              </Typography>
            </Box>
          }
          testIdPrefix="bo_table"
        />
      </Box>
    </Box>
  )
}

function StatCard({ label, primary, secondary, description, onClick, linkLabel, testId }: {
  label: string
  primary: string
  secondary?: string
  description?: string
  onClick?: () => void
  linkLabel?: string
  testId?: string
}) {
  const card = (
    <Box
      data-testid={testId}
      onClick={onClick}
      sx={{
        p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1,
        display: 'flex', flexDirection: 'column', gap: 0.25,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.12s, background-color 0.12s',
        '&:hover': onClick ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
      }}
    >
      <CardLabel label={label} />
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.1 }}>
        {primary}
      </Typography>
      {secondary && (
        <Typography variant="caption" sx={{ fontSize: '0.62rem', opacity: 0.55 }}>
          {secondary}
        </Typography>
      )}
      {onClick && linkLabel && (
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', mt: 0.25, display: 'block' }}>
          {linkLabel}
        </Typography>
      )}
    </Box>
  )
  return description ? <Tooltip title={description}>{card}</Tooltip> : card
}
